/* Copy for the /tools hub.
 *
 * Same contract as the tip splitter's strings file: nothing in the component
 * hardcodes English, so Spanish is a second key here rather than a second
 * component. Spanish is deferred with the rest of the tools.
 */

// The 17-key shape ToolFeedback expects. Each door overrides what it needs and
// inherits the rest, so a wording fix to the shared parts lands everywhere.
const BASE_FORM = {
  eyebrow: "Free tools",
  title: "What should I build next?",
  sub: "",
  name: "Name",
  contact: "Email or phone",
  contactPlaceholder: "you@example.com or (480) 555-0123",
  message: "What you need",
  messagePlaceholder: "",
  send: "Send it",
  sending: "Sending...",
  cancel: "Cancel",
  close: "Close",
  invalid: "Needs a name, a way to reach you, and a note.",
  error: "That didn't send. Try again, or text (480) 282-0569.",
  sent: "Got it, thanks. If I have a follow-up question I'll reach out at what you left.",
  backToTool: "Back to the tools",
  privacy: "Your name and contact come to me and nowhere else.",
};

const form = (over) => ({ ...BASE_FORM, ...over });

export const STRINGS = {
  en: {
    meta: {
      title: "Free Small-Business Tools — QR Codes & Tip Splitting | Daniel Ray Rangel",
      description:
        "Free tools for small businesses: an unlimited QR code generator and a tip split calculator for bars and restaurants. No sign-up, no account, and nothing leaves your browser.",
      ogTitle: "Free Tools for Small Business — QR Codes & Tip Splitting",
      ogDescription:
        "Two free tools for people running a business. No account, nothing uploaded, more on the way.",
    },

    hero: {
      badges: ["Free tools", "No sign-up", "Nothing leaves your browser"],
      titleLine1: "Small tools,",
      titleLine2: "free to use.",
      sub: "Two are ready now and a couple more are coming. There's no account to make and no trial that runs out. If one of them saves you time, use it.",
    },

    live: {
      label: "Ready now",
      titleLine1: "Two tools",
      titleLine2: "you can use today.",
    },

    tools: [
      {
        id: "qr",
        href: "/tools/qr",
        icon: "qr",
        name: "QR Code Generator",
        who: "For anyone printing something",
        body: "Makes codes for Wi-Fi, business cards, links, texts, WhatsApp, map pins, and calendar invites. Pick your colors and shapes, drop your logo in the middle, and download a PNG, a JPG, or an SVG that stays sharp at any print size. It all runs in your browser, so your Wi-Fi password never goes anywhere.",
        meta: "Free · No account · No limit",
        cta: "Open the generator",
      },
      {
        id: "tip-split",
        href: "/tools/tip-split",
        icon: "split",
        name: "Tip Split Calculator",
        who: "For bars, restaurants, and anyone closing out a shift",
        body: "Put in who worked and what the pool was. It splits by hours, or works out the tip-out when servers pay a percentage to the bar and the bussers. Everything is figured in whole cents, so the shares always add up to the pool. Print a breakdown for the crew or save the PDF.",
        meta: "Free · No account · Works on your phone",
        cta: "Open the calculator",
      },
    ],

    roadmap: {
      label: "Coming up",
      titleLine1: "Two more",
      titleLine2: "on the way.",
      sub: "Which one I build first depends on who asks for it. Put your name on one and it moves up the list.",
      badge: "In progress",
      button: "I'd use this",
      items: [
        {
          id: "job-pricing",
          name: "Job Pricing Calculator",
          who: "For contractors and trades",
          body: "Add up materials, hours, and your markup, and see the margin before you send the quote. Set your rate once and it remembers it.",
          request: {
            id: "job-pricing",
            toolName: "Job Pricing Calculator (waitlist)",
            labels: form({
              eyebrow: "Job pricing calculator",
              title: "Want this when it's ready?",
              sub: "Leave your name and a way to reach you and I'll send it when it works. A line about what you price helps me decide what goes in first.",
              message: "What do you price?",
              messagePlaceholder:
                "Tree removal. Materials, two guys for a day, dump fees, 30% on top.",
              send: "Put me on the list",
              sent: "You're on the list. I'll reach out when it's ready.",
            }),
          },
        },
        {
          id: "invoice-pdf",
          name: "Invoice & Estimate PDF",
          who: "For anyone who bills by the job",
          body: "Type in your line items and get a clean PDF with your business name on top. No account, and no fee per invoice.",
          request: {
            id: "invoice-pdf",
            toolName: "Invoice PDF (waitlist)",
            labels: form({
              eyebrow: "Invoice & estimate PDF",
              title: "Want this when it's ready?",
              sub: "Leave your name and a way to reach you and I'll send it when it works. Tell me what you bill for and I'll build the first version around it.",
              message: "What do you bill for?",
              messagePlaceholder:
                "Monthly lawn service. Same six line items every time, and I retype them in Word.",
              send: "Put me on the list",
              sent: "You're on the list. I'll reach out when it's ready.",
            }),
          },
        },
      ],
    },

    why: {
      label: "Why free",
      titleLine1: "Why these",
      titleLine2: "are free.",
      cards: [
        {
          id: "sample",
          icon: "tool",
          title: "Try it, don't read about it",
          body: "Anyone can say they build good software. You'll learn more from using something I made while you were actually working. That part is yours either way.",
        },
        {
          id: "client",
          icon: "users",
          title: "They came from real jobs",
          body: "The tip splitter exists because a manager was doing it on a legal pad. The QR tool exists because a print job needed a vector code and every site I found wanted a subscription for one.",
        },
        {
          id: "nothing",
          icon: "shield",
          title: "There's nothing to collect",
          body: "No account, no email wall, and nothing tracking what you type. Both tools run in your browser, so your roster and your Wi-Fi password never reach me.",
        },
      ],
      note: "Built and looked after by Daniel Rangel at Exhale Software Solutions in Goodyear, AZ. If something breaks, you can get a real person about it.",
    },

    ask: {
      eyebrow: "More on the way",
      text: "If there's something you keep doing by hand or in a spreadsheet at night, tell me about it. If it fits in a browser tab, it can probably be free too.",
      button: "Tell me what to build",
      request: {
        id: "request",
        toolName: "Tool Request",
        labels: form({
          title: "What should I build next?",
          sub: "Something you redo every week, a form you keep retyping, a number that keeps coming out wrong at the end of the day. Tell me what it is and I'll say whether it'd make a decent free tool.",
          messagePlaceholder:
            "Every month I rebuild the same commission sheet in Excel and it breaks when somebody sorts it...",
        }),
      },
    },

    faq: {
      label: "Good to know",
    },
    faqs: [
      {
        q: "Are these really free?",
        a: "Yes. Everything is right on the page. Nothing is locked, there's no watermark to pay off, and there's no limit on how many you make. The QR tool adds a small caption you can switch off before you download, and that's it.",
      },
      {
        q: "Do I need an account?",
        a: "No. There's no sign-up on this site at all. Neither tool asks for your email before it works, or after.",
      },
      {
        q: "Can I use these for my business?",
        a: "Yes. Put the codes on your truck, your menu, or your yard signs. Run payroll off the tip splitter's receipts. Nothing you make with them belongs to me.",
      },
      {
        q: "What happens to what I type in?",
        a: "It stays on your device. Both tools do their math in your browser and don't send what you enter to a server. The tip splitter saves your roster so you don't retype it every shift, which also means clearing your browser data clears it. The only thing that gets sent from this page is a message you type into the feedback form yourself.",
      },
      {
        q: "Can you build one for my business?",
        a: "Yes, that's the work I do. Internal tools, quoting and scheduling systems, Salesforce, and automation for small companies around Phoenix and the West Valley, plus remote work elsewhere. If a version of one of these with your rates and your rules would save someone an hour a day, it's worth a call.",
      },
      {
        q: "How do you pick what's next?",
        a: "By what people ask for here. Both of the ones coming up came from that. If yours is close to something already on the list, it usually jumps ahead.",
      },
    ],

    cta: {
      eyebrow: ["Phoenix", "West Valley", "Remote"],
      titleLine1: "Need one built",
      titleLine2: "for your business?",
      sub: "The same thing, set up for how you work: your rates, your rules, your logo, and nobody retyping it into a spreadsheet after.",
      primary: "Get in Touch",
      secondary: "See Recent Work",
    },
  },

  // Spanish for the tools is deferred with the tools themselves. When it lands
  // it lands here, and no component changes.
  es: null,
};

export default STRINGS;
